import { STATE_KEYS } from './StateConstants.js';

/**
 * Map state management module.
 * Handles map position, tiles, constructions, encounters, story nodes, and traders.
 * Receives parent GameStateManager reference for state access and notifications.
 */
export class MapState {
  constructor(parent) {
    this.parent = parent;
  }

  get state() {
    return this.parent.state;
  }

  _notify(key) {
    this.parent._notify(key);
  }

  // ==========================================
  // MAP POSITION
  // ==========================================

  getMapSeed() {
    return this.state.map.seed;
  }

  getPlayerPosition() {
    return this.state.map.playerPosition;
  }

  setPlayerPosition(q, r) {
    const oldPos = this.state.map.playerPosition;
    console.log('[DEBUG MapState.setPlayerPosition] Changing:', {
      from: oldPos,
      to: { q, r }
    });
    this.state.map.playerPosition = { q, r };
    this._notify(STATE_KEYS.MAP_POSITION);
  }

  // ==========================================
  // EXPLORED TILES
  // ==========================================

  getExploredTiles() {
    return this.state.map.exploredTiles;
  }

  addExploredTile(q, r) {
    const exists = this.state.map.exploredTiles.some(t => t.q === q && t.r === r);

    if (!exists) {
      this.state.map.exploredTiles.push({ q, r });
    }
  }

  // ==========================================
  // VISITED TILES (player has physically stood on tile)
  // ==========================================

  getVisitedTiles() {
    return this.state.map.visitedTiles || [];
  }

  addVisitedTile(q, r) {
    if (!this.state.map.visitedTiles) {
      this.state.map.visitedTiles = [];
    }
    const exists = this.state.map.visitedTiles.some(t => t.q === q && t.r === r);
    if (!exists) {
      this.state.map.visitedTiles.push({ q, r });
    }
  }

  isTileVisited(q, r) {
    return (this.state.map.visitedTiles || []).some(t => t.q === q && t.r === r);
  }

  // ==========================================
  // CONSTRUCTIONS
  // ==========================================

  getConstructions() {
    return this.state.map.constructions || [];
  }

  addConstruction(constructionId, q, r) {
    if (!this.state.map.constructions) {
      this.state.map.constructions = [];
    }
    this.state.map.constructions.push({
      constructionId,
      position: { q, r },
      state: {}
    });
    this._notify(STATE_KEYS.CONSTRUCTIONS);
    this.parent.save();
  }

  getConstructionAt(q, r) {
    const constructions = this.getConstructions();
    return constructions.find(c => c.position.q === q && c.position.r === r) || null;
  }

  hasConstructionAt(q, r) {
    return this.getConstructionAt(q, r) !== null;
  }

  updateConstructionState(q, r, newState) {
    const construction = this.getConstructionAt(q, r);
    if (construction) {
      construction.state = { ...construction.state, ...newState };
      this.parent.save();
      return true;
    }
    return false;
  }

  // ==========================================
  // CONSTRUCTION MOVEMENTS (for Spirit Shrine)
  // ==========================================

  getConstructionMovements() {
    return this.state.map.constructionMovements || 0;
  }

  incrementConstructionMovements() {
    if (!this.state.map.constructionMovements) {
      this.state.map.constructionMovements = 0;
    }
    this.state.map.constructionMovements++;
    this.parent.save();
    return this.state.map.constructionMovements;
  }

  resetConstructionMovements() {
    this.state.map.constructionMovements = 0;
    this.parent.save();
  }

  // ==========================================
  // CLEARED TILES
  // ==========================================

  getClearedTiles() {
    return this.state.map.clearedTiles || [];
  }

  isTileCleared(q, r) {
    const clearedTiles = this.getClearedTiles();
    return clearedTiles.some(t => t.q === q && t.r === r);
  }

  markTileCleared(q, r) {
    if (!this.state.map.clearedTiles) {
      this.state.map.clearedTiles = [];
    }

    if (!this.isTileCleared(q, r)) {
      this.state.map.clearedTiles.push({ q, r });
      this.parent.save();
      return true;
    }
    return false;
  }

  // ==========================================
  // PENDING BATTLE
  // ==========================================

  setPendingBattle(q, r) {
    console.log('[DEBUG MapState.setPendingBattle] Setting:', { q, r });
    this.state.map.pendingBattlePosition = { q, r };
    this.parent.save();
    
    // Verify it was saved to localStorage
    const stored = localStorage.getItem('air_game_state');
    if (stored) {
      const parsed = JSON.parse(stored);
      console.log('[DEBUG MapState.setPendingBattle] Verified in localStorage:', 
        parsed.map?.pendingBattlePosition);
    }
  }

  getPendingBattle() {
    return this.state.map.pendingBattlePosition;
  }

  clearPendingBattle() {
    this.state.map.pendingBattlePosition = null;
    this.parent.save();
  }

  setRecentlyClearedTile(q, r) {
    this.state.map.recentlyClearedTile = { q, r };
    this.parent.save();
  }

  getRecentlyClearedTile() {
    return this.state.map.recentlyClearedTile || null;
  }

  clearRecentlyClearedTile() {
    this.state.map.recentlyClearedTile = null;
    this.parent.save();
  }

  // ==========================================
  // TILE ENCOUNTERS
  // ==========================================

  setTileEnemyType(q, r, enemyType) {
    if (!this.state.map.tileEnemyTypes) {
      this.state.map.tileEnemyTypes = {};
    }
    const key = `${q}_${r}`;
    this.state.map.tileEnemyTypes[key] = enemyType;
    this.parent.save();
  }

  getTileEnemyType(q, r) {
    if (!this.state.map.tileEnemyTypes) {
      return null;
    }
    const key = `${q}_${r}`;
    return this.state.map.tileEnemyTypes[key] || null;
  }

  setTileEncounter(q, r, config) {
    if (!this.state.map.tileEncounters) {
      this.state.map.tileEncounters = {};
    }
    const key = `${q}_${r}`;
    this.state.map.tileEncounters[key] = config;
    this.parent.save();
  }

  getTileEncounter(q, r) {
    if (!this.state.map.tileEncounters) {
      return null;
    }
    const key = `${q}_${r}`;
    return this.state.map.tileEncounters[key] || null;
  }

  updateEncounterRemaining(q, r, remainingEnemies) {
    const encounter = this.getTileEncounter(q, r);
    if (encounter) {
      encounter.remaining = remainingEnemies;
      this.setTileEncounter(q, r, encounter);
    }
  }

  clearTileEncounter(q, r) {
    if (!this.state.map.tileEncounters) {
      return;
    }
    const key = `${q}_${r}`;
    delete this.state.map.tileEncounters[key];
    this.parent.save();
  }

  // ==========================================
  // TILE REWARD BUNDLES
  // ==========================================

  setTileRewardBundle(q, r, rewardBundle) {
    if (!this.state.map.tileRewardBundles) {
      this.state.map.tileRewardBundles = {};
    }
    const key = `${q}_${r}`;
    this.state.map.tileRewardBundles[key] = rewardBundle;
    this.parent.save();
  }

  getTileRewardBundle(q, r) {
    if (!this.state.map.tileRewardBundles) {
      return null;
    }
    const key = `${q}_${r}`;
    return this.state.map.tileRewardBundles[key] || null;
  }

  hasTileRewardBundle(q, r) {
    return this.getTileRewardBundle(q, r) !== null;
  }

  markTileRewardBundleClaimed(q, r) {
    const bundle = this.getTileRewardBundle(q, r);
    if (!bundle) return false;
    bundle.claimed = true;
    this.setTileRewardBundle(q, r, bundle);
    return true;
  }

  clearTileRewardBundle(q, r) {
    if (!this.state.map.tileRewardBundles) return;
    const key = `${q}_${r}`;
    delete this.state.map.tileRewardBundles[key];
    this.parent.save();
  }

  // ==========================================
  // STORY NODES
  // ==========================================

  setStoryNodePlacement(q, r, storyNodeId) {
    if (!this.state.map.storyNodePlacements) {
      this.state.map.storyNodePlacements = {};
    }
    const key = `${q}_${r}`;
    this.state.map.storyNodePlacements[key] = storyNodeId;
    this.parent.save();
  }

  getStoryNodeAt(q, r) {
    if (!this.state.map.storyNodePlacements) {
      return null;
    }
    const key = `${q}_${r}`;
    return this.state.map.storyNodePlacements[key] || null;
  }

  getStoryNodePlacements() {
    return this.state.map.storyNodePlacements || {};
  }

  completeStoryNode(storyNodeId) {
    if (!this.state.map.completedStoryNodes) {
      this.state.map.completedStoryNodes = [];
    }
    if (!this.state.map.completedStoryNodes.includes(storyNodeId)) {
      this.state.map.completedStoryNodes.push(storyNodeId);
      this.parent.save();
      return true;
    }
    return false;
  }

  isStoryNodeComplete(storyNodeId) {
    return (this.state.map.completedStoryNodes || []).includes(storyNodeId);
  }

  getCompletedStoryNodes() {
    return this.state.map.completedStoryNodes || [];
  }

  // ==========================================
  // HEX ANALYSIS (Strategic Analysis memory)
  // ==========================================

  getAnalyzedHexes() {
    return this.state.map.analyzedHexes || [];
  }

  addAnalyzedHex(q, r) {
    if (!this.state.map.analyzedHexes) {
      this.state.map.analyzedHexes = [];
    }
    const exists = this.state.map.analyzedHexes.some(h => h.q === q && h.r === r);
    if (!exists) {
      this.state.map.analyzedHexes.push({ q, r });
      this.parent.save();
      return true;
    }
    return false;
  }

  isHexAnalyzed(q, r) {
    return (this.state.map.analyzedHexes || []).some(h => h.q === q && h.r === r);
  }

  setPendingStoryNode(q, r) {
    this.state.map.pendingStoryNodePosition = { q, r };
    this.parent.save();
  }

  getPendingStoryNode() {
    return this.state.map.pendingStoryNodePosition;
  }

  clearPendingStoryNode() {
    this.state.map.pendingStoryNodePosition = null;
    this.parent.save();
  }

  // ==========================================
  // TRADERS
  // ==========================================

  setTileTrader(q, r, traderData) {
    if (!this.state.map.tileTraders) {
      this.state.map.tileTraders = {};
    }
    const key = `${q}_${r}`;
    this.state.map.tileTraders[key] = traderData;
    this.parent.save();
  }

  getTileTrader(q, r) {
    if (!this.state.map.tileTraders) {
      return null;
    }
    const key = `${q}_${r}`;
    return this.state.map.tileTraders[key] || null;
  }

  isTraderTile(q, r) {
    return this.getTileTrader(q, r) !== null;
  }

  getTraderInventory(q, r) {
    const trader = this.getTileTrader(q, r);
    return trader?.inventory || [];
  }

  purchaseFromTrader(q, r, itemIndex) {
    const trader = this.getTileTrader(q, r);
    if (!trader || !trader.inventory || itemIndex < 0 || itemIndex >= trader.inventory.length) {
      return null;
    }

    const item = trader.inventory[itemIndex];
    trader.inventory.splice(itemIndex, 1);
    this.setTileTrader(q, r, trader);
    return item;
  }

  setPendingTrader(q, r) {
    this.state.map.pendingTraderPosition = { q, r };
    this.parent.save();
  }

  getPendingTrader() {
    return this.state.map.pendingTraderPosition;
  }

  clearPendingTrader() {
    this.state.map.pendingTraderPosition = null;
    this.parent.save();
  }

  setPendingTraderEssence(color) {
    if (!this.state.map) this.state.map = {};
    this.state.map.pendingTraderEssence = color;
    this.parent.save();
  }

  getPendingTraderEssence() {
    return this.state.map?.pendingTraderEssence || null;
  }

  clearPendingTraderEssence() {
    if (this.state.map) {
      this.state.map.pendingTraderEssence = null;
      this.parent.save();
    }
  }

  // ==========================================
  // SETTLEMENTS
  // ==========================================

  setSettlementTrader(q, r, traderData) {
    if (!this.state.map.settlementTraders) {
      this.state.map.settlementTraders = {};
    }
    const key = `${q}_${r}`;
    this.state.map.settlementTraders[key] = traderData;
    this.parent.save();
  }

  getSettlementTrader(q, r) {
    if (!this.state.map.settlementTraders) {
      return null;
    }
    const key = `${q}_${r}`;
    return this.state.map.settlementTraders[key] || null;
  }

  purchaseFromSettlement(q, r, itemIndex) {
    const trader = this.getSettlementTrader(q, r);
    if (!trader || !trader.inventory || itemIndex < 0 || itemIndex >= trader.inventory.length) {
      return null;
    }

    const item = trader.inventory[itemIndex];
    trader.inventory.splice(itemIndex, 1);
    this.setSettlementTrader(q, r, trader);
    return item;
  }

  discoverSettlement(storyNodeId) {
    if (!this.state.map.discoveredSettlements) {
      this.state.map.discoveredSettlements = [];
    }
    if (!this.state.map.discoveredSettlements.includes(storyNodeId)) {
      this.state.map.discoveredSettlements.push(storyNodeId);
      this.parent.save();
      return true;
    }
    return false;
  }

  isSettlementDiscovered(storyNodeId) {
    return (this.state.map.discoveredSettlements || []).includes(storyNodeId);
  }

  getDiscoveredSettlements() {
    return this.state.map.discoveredSettlements || [];
  }

  setLastSettlementPosition(q, r) {
    this.state.map.lastSettlementPosition = { q, r };
    this.parent.save();
  }

  getLastSettlementPosition() {
    return this.state.map.lastSettlementPosition;
  }

  clearLastSettlementPosition() {
    this.state.map.lastSettlementPosition = null;
    this.parent.save();
  }

  // ==========================================
  // HOME SETTLEMENT
  // ==========================================

  setHomePosition(q, r) {
    this.state.map.homePosition = { q, r };
    this.parent.save();
  }

  getHomePosition() {
    return this.state.map.homePosition || null;
  }

  hasHome() {
    return this.state.map.homePosition !== null;
  }

  isHomeTile(q, r) {
    const home = this.getHomePosition();
    return home && home.q === q && home.r === r;
  }

  // ==========================================
  // BLUEPRINTS
  // ==========================================

  getBlueprints() {
    return this.state.blueprints || [];
  }

  hasBlueprint(blueprintId) {
    return this.getBlueprints().includes(blueprintId);
  }

  addBlueprint(blueprintId) {
    if (!this.state.blueprints) {
      this.state.blueprints = [];
    }
    if (!this.hasBlueprint(blueprintId)) {
      this.state.blueprints.push(blueprintId);
      this._notify(STATE_KEYS.BLUEPRINTS);
      this.parent.save();
      return true;
    }
    return false;
  }

  // ==========================================
  // DISCOVERED TRADER ITEMS (for uniqueness tracking)
  // ==========================================

  getDiscoveredTraderEquipment() {
    return this.state.map.discoveredTraderEquipment || [];
  }

  addDiscoveredTraderEquipment(equipmentId) {
    if (!this.state.map.discoveredTraderEquipment) {
      this.state.map.discoveredTraderEquipment = [];
    }
    if (!this.state.map.discoveredTraderEquipment.includes(equipmentId)) {
      this.state.map.discoveredTraderEquipment.push(equipmentId);
      this.parent.save();
      return true;
    }
    return false;
  }

  getDiscoveredTraderBlueprints() {
    return this.state.map.discoveredTraderBlueprints || [];
  }

  addDiscoveredTraderBlueprint(blueprintId) {
    if (!this.state.map.discoveredTraderBlueprints) {
      this.state.map.discoveredTraderBlueprints = [];
    }
    if (!this.state.map.discoveredTraderBlueprints.includes(blueprintId)) {
      this.state.map.discoveredTraderBlueprints.push(blueprintId);
      this.parent.save();
      return true;
    }
    return false;
  }

  // ==========================================
  // CLEAR ALL MAP DATA
  // ==========================================

  clearAllMapData() {
    this.state.map.tileEncounters = {};
    this.state.map.tileRewardBundles = {};
    this.state.map.tileEnemyTypes = {};
    this.state.map.tileTraders = {};
    this.state.map.clearedTiles = [];
    this.state.map.visitedTiles = [];
    this.state.map.storyNodePlacements = {};
    this.state.map.completedStoryNodes = [];
    this.state.map.analyzedHexes = [];
    this.state.map.constructions = [];
    this.state.map.constructionMovements = 0;
    this.state.map.settlementTraders = {};
    this.state.map.discoveredSettlements = [];
    this.state.map.lastSettlementPosition = null;
    this.state.map.homePosition = null;
    this.state.map.discoveredTraderEquipment = [];
    this.state.map.discoveredTraderBlueprints = [];
    this.state.blueprints = [];
    this.parent.save();
    console.log('Map data cleared - refresh to regenerate map');
  }
}
